/**
 * The discount domain's words, taken from the backend that defines them
 * (`rista-be` `catalog/discount_service.saveDiscount`, read 2026-10-11).
 *
 * Discount v2 declares its own vocabulary rather than reading v1's `data/constants`
 * (NEW_ORDER_V2_PLAN.md rule 14). Every value here is the backend's spelling, because the
 * pricing engine and the save compare against these exact strings.
 */

/** A merchant's discount is one of two classes. */
export const DISCOUNT_CLASS = {
  /** A rate the cashier applies (and may edit): priced from the bill entry itself. */
  discount: "Discount",
  /** A configured deal: priced from the merchant's own copy of it, found by its code. */
  deal: "Deal",
} as const;

/** How a discount reaches the bill. */
export const DISCOUNT_MODE = {
  manual: "Manual",
  /** Placed by the pricing engine itself when the bill qualifies. */
  auto: "Auto",
  /** Applied with a code. */
  coupon: "Coupon",
} as const;

/** What a discount takes off. */
export const DISCOUNT_TYPE = {
  absolute: "Absolute",
  percentage: "Percentage",
  voucher: "Voucher",
  buyXGetY: "BuyXGetY",
  buyXGetX: "BuyXGetX",
  buyX: "BuyX",
  buyXReplaceY: "BuyXReplaceY",
  slab: "SlabDiscount",
} as const;

/** The three ways a `BuyXGetY` deal picks the free item. */
export const BUY_X_GET_Y_VARIANT = {
  leastExpensiveOfSet: "BuyXGetYLeastExpensiveOfSet",
  mostExpensiveOfSet: "BuyXGetYMostExpensiveOfSet",
  leastExpensiveOfSale: "BuyXGetYLeastExpensiveOfSale",
} as const;

/** `source` on an offer a partner sent: the engine and the save route these differently. */
export const EXTERNAL_SOURCE = "external";

/** A sale's `sourceInfo.source` when it came in through the API: such a sale is not the terminal's to release. */
export const API_SALE_SOURCE = "API";

/**
 * The kinds of bill entry, by what the pricing engine reads off each
 * (docs/architecture/discounts/bill-entry-readers.md §2, §5).
 */
export type EntryKind = "catalogueDeal" | "typedCoupon" | "manualDiscount" | "partnerOffer" | "reward";
